import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Check, X, Users, MapPin, CalendarDays, Crown, DollarSign, Star, RefreshCw, Mail, BadgeCheck, UserPlus, BarChart3, MessageSquare, Download } from "lucide-react";
import AdminAnalytics from "../components/AdminAnalytics";
import AdminFeedback from "../components/AdminFeedback";
import AdminScrapers from "../components/AdminScrapers";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState({ venues: [], events: [] });
  const [approvedEvents, setApprovedEvents] = useState([]);
  const [claims, setClaims] = useState([]);
  const [allVenues, setAllVenues] = useState([]);
  const [assignInputs, setAssignInputs] = useState({}); // {venueId: email}
  const [newsForm, setNewsForm] = useState({ title: "", excerpt: "", content: "", cover_image: "", tagsInput: "" });
  const [newFeedbackCount, setNewFeedbackCount] = useState(0);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  const reload = async () => {
    const [s, sub, evs, cls, vs, fb] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/submissions"),
      api.get("/admin/events"),
      api.get("/admin/claims?status=pending"),
      api.get("/venues?limit=500"),
      api.get("/admin/feedback?status=new&limit=1"),
    ]);
    setStats(s.data);
    setSubmissions(sub.data);
    setApprovedEvents(evs.data);
    setClaims(cls.data);
    setAllVenues(vs.data);
    setNewFeedbackCount(fb.data?.counts?.new || 0);
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  const approveAllEvents = async () => {
    if (!window.confirm(`Approve all ${submissions.events.length} pending events? This will notify each owner by email.`)) return;
    try {
      const r = await api.post("/admin/events/approve-all");
      toast.success(`Approved ${r.data.approved} events`);
      reload();
    } catch { toast.error("Bulk approve failed"); }
  };

  const approve = async (type, id) => {
    try {
      await api.post(`/admin/${type}/${id}/approve`);
      toast.success("Approved");
      reload();
    } catch { toast.error("Failed"); }
  };

  const reject = async (type, id) => {
    try {
      await api.post(`/admin/${type}/${id}/reject`);
      toast.success("Rejected");
      reload();
    } catch { toast.error("Failed"); }
  };

  const togglePin = async (id) => {
    try {
      const r = await api.post(`/admin/events/${id}/pin`);
      toast.success(r.data.editors_pick ? "Pinned as editor's pick" : "Unpinned");
      reload();
    } catch { toast.error("Failed"); }
  };

  const approveClaim = async (id) => {
    try {
      await api.post(`/admin/claims/${id}/approve`);
      toast.success("Ownership transferred ✓");
      reload();
    } catch { toast.error("Failed"); }
  };

  const rejectClaim = async (id) => {
    try {
      await api.post(`/admin/claims/${id}/reject`, { reason: "Not verified" });
      toast.success("Claim rejected");
      reload();
    } catch { toast.error("Failed"); }
  };

  const assignOwner = async (venueId) => {
    const email = (assignInputs[venueId] || "").trim().toLowerCase();
    if (!email) { toast.error("Enter the owner's email"); return; }
    try {
      const r = await api.post(`/admin/venues/${venueId}/assign-owner`, { user_email: email });
      toast.success(`Owner assigned: ${r.data.owner.name || r.data.owner.email}`);
      setAssignInputs((s) => ({ ...s, [venueId]: "" }));
      reload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed";
      toast.error(msg);
    }
  };

  const releaseOwner = async (venueId) => {
    try {
      await api.post(`/admin/venues/${venueId}/release-owner`);
      toast.success("Ownership released");
      reload();
    } catch { toast.error("Failed"); }
  };

  const runDigest = async () => {
    try {
      const r = await api.post("/admin/digest/run");
      toast.success(`Weekly digest sent to ${r.data.sent} owner${r.data.sent === 1 ? "" : "s"} (${r.data.skipped} skipped)`);
    } catch {
      toast.error("Digest failed");
    }
  };

  const publishNews = async (e) => {
    e.preventDefault();
    try {
      const tags = newsForm.tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post("/news", {
        title: newsForm.title,
        excerpt: newsForm.excerpt,
        content: newsForm.content,
        cover_image: newsForm.cover_image || null,
        tags,
      });
      toast.success("Story published");
      setNewsForm({ title: "", excerpt: "", content: "", cover_image: "", tagsInput: "" });
    } catch { toast.error("Failed"); }
  };

  if (!user || user.role !== "admin") return null;

  const statCards = stats ? [
    { label: "Total venues", value: stats.total_venues, Icon: MapPin },
    { label: "Pending review", value: stats.pending_submissions, Icon: CalendarDays },
    { label: "Events", value: stats.total_events, Icon: CalendarDays },
    { label: "Users", value: stats.total_users, Icon: Users },
    { label: "Sponsored", value: stats.sponsored_venues, Icon: Crown },
    { label: "Paid transactions", value: stats.paid_transactions, Icon: DollarSign },
  ] : [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <span className="text-sm tracking-widest text-coastal uppercase">Admin</span>
        <div className="flex items-center justify-between mt-2 mb-8 flex-wrap gap-3">
          <h1 className="font-heading text-4xl text-navy tracking-tight" data-testid="admin-title">Control Room</h1>
          <Button
            onClick={() => { reload(); toast.success("Refreshed"); }}
            variant="outline"
            className="rounded-full"
            data-testid="admin-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            onClick={runDigest}
            variant="outline"
            className="rounded-full border-coastal/30 text-coastal hover:bg-coastal hover:text-white"
            data-testid="admin-digest-now"
          >
            <Mail className="w-4 h-4 mr-2" /> Send weekly digest now
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-black/5 rounded-2xl p-5">
              <s.Icon className="w-4 h-4 text-coastal mb-2" />
              <div className="text-2xl font-heading text-navy">{s.value}</div>
              <div className="text-xs text-neutral-500">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="submissions">
          <TabsList>
            <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims">
              Ownership claims {claims.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-coastal text-white">{claims.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="owners" data-testid="tab-owners">Venue owners</TabsTrigger>
            <TabsTrigger value="picks" data-testid="tab-picks">Editor&apos;s picks</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news">Publish story</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback" className="gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Feedback
              {newFeedbackCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-coastal text-white">{newFeedbackCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="imports" data-testid="tab-imports" className="gap-1.5">
              <Download className="w-3.5 h-3.5" /> Imports
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" /> Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-6">
            <h2 className="font-heading text-2xl text-navy mb-4">Venues awaiting review ({submissions.venues.length})</h2>
            {submissions.venues.length === 0 ? (
              <p className="text-neutral-500">All clear — no pending venues.</p>
            ) : (
              <div className="space-y-3">
                {submissions.venues.map((v) => (
                  <div key={v.id} className="bg-white border border-black/5 rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`pending-venue-${v.id}`}>
                    <div>
                      <div className="font-heading text-lg text-navy">{v.name}</div>
                      <div className="text-sm text-neutral-500 capitalize">{v.category} · {v.location}</div>
                      <div className="text-sm text-neutral-600 mt-1 line-clamp-2">{v.description}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => reject("venues", v.id)} data-testid={`reject-${v.id}`} className="rounded-full">
                        <X className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => approve("venues", v.id)} data-testid={`approve-${v.id}`} className="rounded-full bg-navy hover:bg-navy/90">
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {submissions.events.length > 0 && (
              <>
                <div className="flex items-center justify-between gap-4 mb-4 mt-10 flex-wrap">
                  <h2 className="font-heading text-2xl text-navy">Events awaiting review ({submissions.events.length})</h2>
                  <Button
                    onClick={approveAllEvents}
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="approve-all-events-btn"
                  >
                    <Check className="w-4 h-4 mr-1.5" /> Approve all ({submissions.events.length})
                  </Button>
                </div>
                <div className="space-y-3">
                  {submissions.events.map((e) => (
                    <div key={e.id} className="bg-white border border-black/5 rounded-2xl p-5 flex items-center justify-between gap-4">
                      <div>
                        <div className="font-heading text-lg text-navy">{e.title}</div>
                        <div className="text-sm text-neutral-500">{new Date(e.start_time).toLocaleString()}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => reject("events", e.id)} data-testid={`reject-event-${e.id}`} className="rounded-full">
                          <X className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={() => approve("events", e.id)} className="rounded-full bg-navy hover:bg-navy/90">
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="claims" className="mt-6">
            <div className="bg-coastal/10 border border-coastal/30 rounded-2xl p-5 mb-6">
              <h2 className="font-heading text-xl text-navy mb-1 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-coastal" /> Ownership claims
              </h2>
              <p className="text-sm text-neutral-600">
                A venue owner has requested to take over a listing. Approving transfers the venue to their account; they&apos;ll be able to edit photos, hours, run sponsorship and reply to reviews.
              </p>
            </div>
            {claims.length === 0 ? (
              <p className="text-neutral-500" data-testid="no-claims">All clear — no pending claims.</p>
            ) : (
              <div className="space-y-3">
                {claims.map((c) => (
                  <div key={c.id} className="bg-white border border-black/5 rounded-2xl p-5 flex items-start justify-between gap-4" data-testid={`claim-row-${c.id}`}>
                    <div className="flex-1">
                      <div className="font-heading text-lg text-navy">{c.venue_name}</div>
                      <div className="text-sm text-neutral-500">
                        From <strong className="text-navy">{c.claimant_name || c.claimant_email}</strong> · {c.claimant_email}
                      </div>
                      {c.message && (
                        <div className="mt-3 text-sm text-neutral-700 bg-neutral-50 rounded-xl p-3 whitespace-pre-wrap">
                          {c.message}
                        </div>
                      )}
                      {c.proof && (
                        <div className="mt-2 text-xs text-coastal">
                          Proof: <a href={c.proof} target="_blank" rel="noreferrer" className="underline break-all">{c.proof}</a>
                        </div>
                      )}
                      <div className="text-xs text-neutral-400 mt-2">
                        Submitted {new Date(c.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => rejectClaim(c.id)} data-testid={`reject-claim-${c.id}`} className="rounded-full">
                        <X className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => approveClaim(c.id)} data-testid={`approve-claim-${c.id}`} className="rounded-full bg-navy hover:bg-navy/90">
                        <Check className="w-4 h-4 mr-1" /> Approve & transfer
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="owners" className="mt-6">
            <div className="bg-sand/20 border border-sand/40 rounded-2xl p-5 mb-6">
              <h2 className="font-heading text-xl text-navy mb-1 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-coastal" /> Venue owners
              </h2>
              <p className="text-sm text-neutral-600">
                Directly assign or release ownership of any venue. Use this when an owner has reached out but hasn&apos;t submitted a formal claim. The user must be registered first.
              </p>
            </div>
            <div className="space-y-3">
              {allVenues.map((v) => (
                <div key={v.id} className="bg-white border border-black/5 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`owner-row-${v.id}`}>
                  <div className="min-w-0">
                    <div className="font-heading text-base text-navy truncate">{v.name}</div>
                    <div className="text-xs text-neutral-500 capitalize">
                      {v.category} · {v.created_by ? <span className="text-emerald-600">owned</span> : <span className="text-amber-600">ownerless</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-[280px] justify-end">
                    <Input
                      placeholder="owner@email.com"
                      value={assignInputs[v.id] || ""}
                      onChange={(e) => setAssignInputs((s) => ({ ...s, [v.id]: e.target.value }))}
                      className="max-w-[260px] h-9"
                      data-testid={`assign-input-${v.id}`}
                    />
                    <Button size="sm" onClick={() => assignOwner(v.id)} className="rounded-full bg-navy hover:bg-navy/90" data-testid={`assign-btn-${v.id}`}>
                      Assign
                    </Button>
                    {v.created_by && (
                      <Button size="sm" variant="outline" onClick={() => releaseOwner(v.id)} className="rounded-full" data-testid={`release-btn-${v.id}`}>
                        Release
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="picks" className="mt-6">
            <div className="bg-sand/20 border border-sand/40 rounded-2xl p-5 mb-6">
              <h2 className="font-heading text-xl text-navy mb-1 flex items-center gap-2">
                <Star className="w-5 h-5 text-sponsored fill-sponsored" /> Editor&apos;s picks
              </h2>
              <p className="text-sm text-neutral-600">
                Pin standout events to the top of <strong>/events</strong> and the home page. Free for the venue, curated by you. Toggle off any time.
              </p>
            </div>
            {approvedEvents.length === 0 ? (
              <p className="text-neutral-500">No approved events yet.</p>
            ) : (
              <div className="space-y-3">
                {approvedEvents.map((e) => (
                  <div key={e.id} className="bg-white border border-black/5 rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`pick-row-${e.id}`}>
                    <div className="flex-1">
                      <div className="font-heading text-lg text-navy flex items-center gap-2">
                        {e.title}
                        {e.editors_pick && <Star className="w-4 h-4 text-sponsored fill-sponsored" />}
                      </div>
                      <div className="text-sm text-neutral-500">
                        {new Date(e.start_time).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                        {" · "}{e.location}
                      </div>
                      {e.editors_pick && e.editors_pick_expires_at && (
                        <div className="text-xs text-coastal mt-1">
                          ⏱️ Auto-expires {new Date(e.editors_pick_expires_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                      {e.editors_pick && !e.editors_pick_expires_at && (
                        <div className="text-xs text-coastal mt-1">♾️ Persists with each recurrence</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => togglePin(e.id)}
                      className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${e.editors_pick ? "bg-navy" : "bg-neutral-300"}`}
                      data-testid={`toggle-pick-${e.id}`}
                      aria-pressed={!!e.editors_pick}
                    >
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${e.editors_pick ? "translate-x-6" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="news" className="mt-6">
            <form onSubmit={publishNews} className="bg-white border border-black/5 rounded-2xl p-8 space-y-4 max-w-3xl">
              <Input placeholder="Title" value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })} required data-testid="news-title-input" />
              <Input placeholder="Excerpt (1 line summary)" value={newsForm.excerpt} onChange={(e) => setNewsForm({ ...newsForm, excerpt: e.target.value })} />
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-white transition-colors text-sm" data-testid="news-cover-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="news-cover-upload"
                      onChange={async (ev) => {
                        const file = ev.target.files?.[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append("file", file);
                        try {
                          const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          setNewsForm({ ...newsForm, cover_image: r.data.url });
                          toast.success("Cover image uploaded");
                        } catch {
                          toast.error("Upload failed");
                        } finally {
                          ev.target.value = "";
                        }
                      }}
                    />
                    Upload cover image
                  </label>
                  {newsForm.cover_image && (
                    <button type="button" onClick={() => setNewsForm({ ...newsForm, cover_image: "" })} className="text-xs text-rose-500 hover:underline">
                      Clear
                    </button>
                  )}
                </div>
                <Input placeholder="…or paste an image URL" value={newsForm.cover_image} onChange={(e) => setNewsForm({ ...newsForm, cover_image: e.target.value })} data-testid="news-cover-url-input" />
                {newsForm.cover_image && (
                  <img src={newsForm.cover_image} alt="cover preview" className="w-full max-w-md aspect-[16/9] object-cover rounded-lg border border-black/5" data-testid="news-cover-preview" />
                )}
              </div>
              <Input placeholder="Tags (comma separated)" value={newsForm.tagsInput} onChange={(e) => setNewsForm({ ...newsForm, tagsInput: e.target.value })} />
              <Textarea rows={10} placeholder="Story content..." value={newsForm.content} onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })} required data-testid="news-content-input" />
              <Button type="submit" className="rounded-full bg-navy hover:bg-navy/90" data-testid="publish-news-btn">Publish story</Button>
            </form>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <AdminAnalytics />
          </TabsContent>

          <TabsContent value="feedback" className="mt-6">
            <AdminFeedback />
          </TabsContent>

          <TabsContent value="imports" className="mt-6">
            <AdminScrapers />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
